import { Injectable, inject } from '@angular/core';
import { InMemoryHttpClient } from './in-memory-http-client';
import { Observable } from 'rxjs';
import { RequestInfo, ResponseOptions, STATUS } from 'angular-in-memory-web-api';
import { TestStoreDataService } from './test-store-data.service';
import {
  AddStoreBankingSettingsRequest,
  AddStoreClosureRequest,
  AddStoreDefaultWorkingHoursRequest,
  AddStoreEmployeeRequirementRequest,
  AddStoreSpecialWorkingHoursRequest,
  EditStoreBankingSettingsRequest,
  EditStoreClosureRequest,
  EditStoreDefaultWorkingHoursRequest,
  EditStoreEmployeeRequirementRequest,
  EditStoreSpecialWorkingHoursRequest
} from '../../../interfaces/request/StoreRequests';

/**
 * This service intercepts {@link StoreService} requests and redirects them to an in-memory data store.
 */
@Injectable({
  providedIn: 'root',
})
export class InMemoryStoreDataService implements InMemoryHttpClient {
  private testStoreDataService = inject(TestStoreDataService);


  /**
   * Intercepts HTTP DELETE request of {@link StoreService}. Deletes a record from an in-memory data store.
   * @param reqInfo information about the current HTTP DELETE request.
   */
  delete(reqInfo: RequestInfo): Observable<ResponseOptions> {
    return reqInfo.utils.createResponse$(() => {
      if (reqInfo.req.url.includes('default-working-hours')) {
        const date = reqInfo.req.url.split('default-working-hours/')[1];
        return this.deleteDefaultWorkingTimes(date);
      }
      if (reqInfo.req.url.includes('special-working-hours')) {
        const date = reqInfo.req.url.split('special-working-hours/')[1];
        return this.deleteSpecialWorkingTimes(date);
      }
      if (reqInfo.req.url.includes('closures')) {
        const date = reqInfo.req.url.split('closures/')[1];
        return this.deleteClosure(date);
      }
      if (reqInfo.req.url.includes('employee-requirements')) {
        const date = reqInfo.req.url.split('requirements/')[1];
        return this.deleteRequirements(date);
      }
      if (reqInfo.req.url.includes('banking-settings')) {
        const date = reqInfo.req.url.split('banking-settings/')[1];
        return this.deleteBanking(date);
      }
    });
  }

  /**
   * Intercepts HTTP GET request of {@link StoreService}. Gets all required records from an in-memory data store.
   * @param reqInfo information about the current HTTP GET request.
   */
  get(reqInfo: RequestInfo): Observable<ResponseOptions> {
    return reqInfo.utils.createResponse$(() => {
      if (reqInfo.id) {
        return this.getSingleStoreDetails(reqInfo.id);
      }
      return this.getAll();
    });
  }

  /**
   * Intercepts HTTP POST request of {@link StoreService}. Inserts a record to in-memory data store.
   * @param reqInfo information about the current HTTP POST request.
   */
  post(reqInfo: RequestInfo): Observable<ResponseOptions> {
    return reqInfo.utils.createResponse$(() => {
      const requestBody = reqInfo.utils.getJsonBody(reqInfo.req);
      if (reqInfo.req.url.includes('default-working-hours')) {
        return this.saveDefaultWorkingTimes(requestBody);
      }
      if (reqInfo.req.url.includes('special-working-hours')) {
        return this.saveSpecialWorkingTimes(requestBody);
      }
      if (reqInfo.req.url.includes('employee-requirements')) {
        return this.saveRequirements(requestBody);
      }
      if (reqInfo.req.url.includes('banking-settings')) {
        return this.saveBanking(requestBody);
      }
      if (reqInfo.req.url.includes('closures')) {
        return this.saveClosure(requestBody);
      }
    });
  }

  /**
   * Intercepts HTTP PUT request of {@link StoreService}.
   * @param reqInfo information about the current HTTP PUT request.
   */
  put(reqInfo: RequestInfo): Observable<ResponseOptions> {
    return reqInfo.utils.createResponse$(() => {
      const requestBody = reqInfo.utils.getJsonBody(reqInfo.req);
      if (reqInfo.req.url.includes('default-working-hours')) {
        const date = reqInfo.req.url.split('default-working-hours/')[1];
        return this.editDefaultWorkingTimes(date, requestBody);
      }
      if (reqInfo.req.url.includes('special-working-hours')) {
        const date = reqInfo.req.url.split('special-working-hours/')[1];
        return this.editSpecialWorkingTimes(date, requestBody);
      }
      if (reqInfo.req.url.includes('closures')) {
        const date = reqInfo.req.url.split('closures/')[1];
        return this.editClosure(date, requestBody);
      }
      if (reqInfo.req.url.includes('employee-requirements')) {
        const date = reqInfo.req.url.split('requirements/')[1];
        return this.editRequirements(date, requestBody);
      }
      if (reqInfo.req.url.includes('banking-settings')) {
        const date = reqInfo.req.url.split('banking-settings/')[1];
        return this.editBanking(date, requestBody);
      }
    });
  }

  /**
   * Intercepts HTTP PATCH request of {@link StoreService}.
   * @param reqInfo information about the current HTTP PATCH request.
   */
  patch(reqInfo: RequestInfo): Observable<ResponseOptions> {
    return undefined;
  }

  private getAll(): ResponseOptions {
    return {
      body: this.testStoreDataService.stores,
      status: STATUS.OK
    };
  }

  private getSingleStoreDetails(storeId: string): ResponseOptions {
    const foundStore = this.testStoreDataService.stores.find(store => store.slug === storeId);
    return {
      body: this.testStoreDataService.getStoreDetails(foundStore),
      status: STATUS.OK
    };
  }

  private saveDefaultWorkingTimes(request: AddStoreDefaultWorkingHoursRequest): ResponseOptions {
    this.testStoreDataService.defaultStoreWorkingTimes.push({
      date: request.date,
      monday: { openingTime: request.monday.openingTime, closingTime: request.monday.closingTime },
      tuesday: { openingTime: request.tuesday.openingTime, closingTime: request.tuesday.closingTime },
      wednesday: { openingTime: request.wednesday.openingTime, closingTime: request.wednesday.closingTime },
      thursday: { openingTime: request.thursday.openingTime, closingTime: request.thursday.closingTime },
      friday: { openingTime: request.friday.openingTime, closingTime: request.friday.closingTime },
      saturday: { openingTime: request.saturday.openingTime, closingTime: request.saturday.closingTime },
      sunday: { openingTime: request.sunday.openingTime, closingTime: request.sunday.closingTime }
    });

    return {
      status: STATUS.CREATED
    };
  }

  private editDefaultWorkingTimes(date: string, request: EditStoreDefaultWorkingHoursRequest): ResponseOptions {
    const index = this.testStoreDataService.defaultStoreWorkingTimes.findIndex(item => item.date === date);
    if (index !== -1) {
      this.testStoreDataService.defaultStoreWorkingTimes.splice(index, 1);
    }
    this.testStoreDataService.defaultStoreWorkingTimes.push({
      date,
      monday: { openingTime: request.monday.openingTime, closingTime: request.monday.closingTime },
      tuesday: { openingTime: request.tuesday.openingTime, closingTime: request.tuesday.closingTime },
      wednesday: { openingTime: request.wednesday.openingTime, closingTime: request.wednesday.closingTime },
      thursday: { openingTime: request.thursday.openingTime, closingTime: request.thursday.closingTime },
      friday: { openingTime: request.friday.openingTime, closingTime: request.friday.closingTime },
      saturday: { openingTime: request.saturday.openingTime, closingTime: request.saturday.closingTime },
      sunday: { openingTime: request.sunday.openingTime, closingTime: request.sunday.closingTime }
    });
    return {
      status: STATUS.NO_CONTENT
    };
  }

  private deleteDefaultWorkingTimes(date: string): ResponseOptions {
    const index = this.testStoreDataService.defaultStoreWorkingTimes.findIndex(item => item.date === date);
    if (index !== -1) {
      this.testStoreDataService.defaultStoreWorkingTimes.splice(index, 1);
    }

    return {
      status: STATUS.OK
    };
  }

  private saveSpecialWorkingTimes(request: AddStoreSpecialWorkingHoursRequest): ResponseOptions {
    this.testStoreDataService.specialStoreWorkingTimes.push({
      date: request.date,
      workingHours: { openingTime: request.workingHours.openingTime, closingTime: request.workingHours.closingTime },
    });

    return {
      status: STATUS.CREATED
    };
  }

  private editSpecialWorkingTimes(date: string, request: EditStoreSpecialWorkingHoursRequest): ResponseOptions {
    const index = this.testStoreDataService.specialStoreWorkingTimes.findIndex(item => item.date === date);
    if (index !== -1) {
      this.testStoreDataService.specialStoreWorkingTimes.splice(index, 1);
    }
    this.testStoreDataService.specialStoreWorkingTimes.push({
      date,
      workingHours: { openingTime: request.workingHours.openingTime, closingTime: request.workingHours.closingTime },
    });
    return {
      status: STATUS.OK
    };
  }

  private deleteSpecialWorkingTimes(date: string): ResponseOptions {
    const index = this.testStoreDataService.specialStoreWorkingTimes.findIndex(item => item.date === date);
    if (index !== -1) {
      this.testStoreDataService.specialStoreWorkingTimes.splice(index, 1);
    }

    return {
      status: STATUS.OK
    };
  }

  private saveRequirements(request: AddStoreEmployeeRequirementRequest): ResponseOptions {
    this.testStoreDataService.storeEmployeeRequirements.push({
      date: request.date,
      minimumOpeningEmployees: request.minimumOpeningEmployees,
      minimumRegularEmployees: request.minimumRegularEmployees,
      minimumClosingEmployees: request.minimumClosingEmployees
    });

    return {
      status: STATUS.CREATED
    };
  }

  private editRequirements(date: string, request: EditStoreEmployeeRequirementRequest): ResponseOptions {
    const index = this.testStoreDataService.storeEmployeeRequirements.findIndex(item => item.date === date);
    if (index !== -1) {
      this.testStoreDataService.storeEmployeeRequirements.splice(index, 1);
    }
    this.testStoreDataService.storeEmployeeRequirements.push({
      date,
      minimumOpeningEmployees: request.minimumOpeningEmployees,
      minimumRegularEmployees: request.minimumRegularEmployees,
      minimumClosingEmployees: request.minimumClosingEmployees
    });

    return {
      status: STATUS.CREATED
    };
  }

  private deleteRequirements(date: string): ResponseOptions {
    const index = this.testStoreDataService.storeEmployeeRequirements.findIndex(item => item.date === date);
    if (index !== -1) {
      this.testStoreDataService.storeEmployeeRequirements.splice(index, 1);
    }

    return {
      status: STATUS.OK
    };
  }

  private saveBanking(request: AddStoreBankingSettingsRequest): ResponseOptions {
    this.testStoreDataService.storeBanking.push({
      date: request.date,
      bankingDays: request.bankingDays
    });

    return {
      status: STATUS.CREATED
    };
  }

  private editBanking(date: string, request: EditStoreBankingSettingsRequest): ResponseOptions {
    const index = this.testStoreDataService.storeBanking.findIndex(item => item.date === date);
    if (index !== -1) {
      this.testStoreDataService.storeBanking.splice(index, 1);
    }
    this.testStoreDataService.storeBanking.push({
      date,
      bankingDays: request.bankingDays
    });

    return {
      status: STATUS.CREATED
    };
  }

  private deleteBanking(date: string): ResponseOptions {
    const index = this.testStoreDataService.storeBanking.findIndex(item => item.date === date);
    if (index !== -1) {
      this.testStoreDataService.storeBanking.splice(index, 1);
    }

    return {
      status: STATUS.OK
    };
  }

  private saveClosure(request: AddStoreClosureRequest): ResponseOptions {
    this.testStoreDataService.storeClosures.push({
      date: request.date,
      reason: request.reason
    });

    return {
      status: STATUS.CREATED
    };
  }

  private editClosure(date: string, request: EditStoreClosureRequest): ResponseOptions {
    const index = this.testStoreDataService.storeClosures.findIndex(item => item.date === date);
    if (index !== -1) {
      this.testStoreDataService.storeClosures.splice(index, 1);
    }
    this.testStoreDataService.storeClosures.push({
      date,
      reason: request.reason
    });

    return {
      status: STATUS.OK
    };
  }

  private deleteClosure(date: string): ResponseOptions {
    const index = this.testStoreDataService.storeClosures.findIndex(item => item.date === date);
    if (index !== -1) {
      this.testStoreDataService.storeClosures.splice(index, 1);
    }

    return {
      status: STATUS.OK
    };
  }
}
